import Cookies from "js-cookie";
import { z } from "zod";

import {
  HUBSPOT_TOKEN_COOKIE_KEY,
  PENDING_INVITE_UUID_STORAGE_KEY,
  REGISTRATION_PARAMS_STORAGE_KEY,
} from "~/helpers/consts";

const RegistrationMarketingParamsSchema = z.object({
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_source: z.string().optional(),
  utm_term: z.string().optional(),
  ref: z.string().optional(),
  subscription_uuid: z.string().optional(),
});

type RegistrationMarketingParams = z.infer<
  typeof RegistrationMarketingParamsSchema
>;

type RegistrationParams = z.infer<typeof RegistrationMarketingParamsSchema> & {
  hutk?: string;
  invitation_uuid?: string;
};

export function saveRegistrationParamsToStorage(): void {
  const search = window.location.search;
  if (!search) return;

  const searchParams = new URLSearchParams(search);
  const parsed = RegistrationMarketingParamsSchema.safeParse(
    Object.fromEntries(searchParams.entries()),
  );

  if (!parsed.success) {
    return;
  }

  const data = parsed.data;
  const hasAnyValue = Object.values(data).some((v) => v !== undefined);

  if (!hasAnyValue) {
    return;
  }

  localStorage.setItem(REGISTRATION_PARAMS_STORAGE_KEY, JSON.stringify(data));
}

export function getRegistrationParams(): RegistrationParams {
  let marketingParams: RegistrationMarketingParams = {};
  const raw = localStorage.getItem(REGISTRATION_PARAMS_STORAGE_KEY);

  if (raw) {
    try {
      const json = JSON.parse(raw);
      marketingParams = RegistrationMarketingParamsSchema.parse(json);
    } catch {
      // just remove broken JSON and consider as none
      localStorage.removeItem(REGISTRATION_PARAMS_STORAGE_KEY);
    }
  }

  const hutk = Cookies.get(HUBSPOT_TOKEN_COOKIE_KEY) ?? undefined;
  const invitation_uuid =
    localStorage.getItem(PENDING_INVITE_UUID_STORAGE_KEY) ?? undefined;

  return {
    ...marketingParams,
    ...(hutk ? { hutk } : {}),
    ...(invitation_uuid ? { invitation_uuid } : {}),
  };
}

export function clearRegistrationParams() {
  window.localStorage.removeItem(REGISTRATION_PARAMS_STORAGE_KEY);
}

export function clearInviteUuid() {
  window.localStorage.removeItem(PENDING_INVITE_UUID_STORAGE_KEY);
}
